import { useMemo, useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import type { Entity } from '@cortex/domain';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { StatusBadge, toneForLifecycleStatus } from '@/components/ui/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';

/**
 * Master pane for the Entities tab.
 *
 * Pure presentation — data and selection come from props so the parent
 * (`EntitiesTab`) owns network fetches and side effects. Built on the
 * shadcn primitives (`Input`, `Button`, `Skeleton`) and the shared
 * `StatusBadge` / `EmptyState` so it matches every other admin surface.
 *
 * UX choices:
 *   - Sticky filter bar at the top so the controls don't scroll away
 *     when the brokerage list gets long.
 *   - Search matches against `name` and `tradingName` on the client to
 *     keep the interaction snappy.
 *   - Empty state distinguishes first-run ("no entities yet") from
 *     filtered-to-nothing, per the design system's five-states rule.
 */

const FILTER_SELECT =
  'h-8 rounded-md border border-input bg-background px-2 text-xs shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none';

export interface EntityListProps {
  readonly entities: readonly Entity[];
  readonly selectedEntityId: string | null;
  readonly isLoading: boolean;
  readonly errorMessage?: string | null;
  readonly onSelect: (entityId: string) => void;
  readonly onCreateNew: () => void;
}

type StatusFilter = 'all' | 'Active' | 'Suspended';
type TypeFilter = 'all' | string;

export function EntityList({
  entities,
  selectedEntityId,
  isLoading,
  errorMessage,
  onSelect,
  onCreateNew,
}: EntityListProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');

  const knownTypes = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entities) map.set(e.entityTypeSlug, e.entityTypeDisplayName);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [entities]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entities.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (typeFilter !== 'all' && e.entityTypeSlug !== typeFilter) return false;
      if (needle.length === 0) return true;
      if (e.name.toLowerCase().includes(needle)) return true;
      if ((e.tradingName ?? '').toLowerCase().includes(needle)) return true;
      return false;
    });
  }, [entities, statusFilter, typeFilter, search]);

  const hasFilters = statusFilter !== 'all' || typeFilter !== 'all' || search !== '';

  return (
    <>
      <div className="space-y-2 border-b border-border bg-muted/40 px-3 py-3">
        <div className="flex gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={cn(FILTER_SELECT, 'flex-1')}
            aria-label="Filter by entity type"
          >
            <option value="all">All types</option>
            {knownTypes.map(([slug, label]) => (
              <option key={slug} value={slug}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className={FILTER_SELECT}
            aria-label="Filter by status"
          >
            <option value="all">All status</option>
            <option value="Active">Active</option>
            <option value="Suspended">Suspended</option>
          </select>
        </div>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or trading name…"
          className="h-8 text-xs"
          aria-label="Search entities"
        />
        <Button type="button" onClick={onCreateNew} size="sm" className="w-full">
          <Plus className="h-4 w-4" aria-hidden="true" />
          New entity
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <ListSkeleton />}
        {!isLoading && errorMessage && (
          <div
            className="border-b border-destructive/20 bg-destructive/10 px-4 py-6 text-center text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}
        {!isLoading && !errorMessage && filtered.length === 0 && (
          <EmptyState
            icon={Building2}
            title={hasFilters ? 'No entities match these filters.' : 'No entities yet.'}
            description={
              hasFilters
                ? 'Try clearing the search or selecting “All types”.'
                : 'Use “New entity” above to add your first brokerage.'
            }
          />
        )}
        {!isLoading && !errorMessage && filtered.length > 0 && (
          <ul className="divide-y divide-border">
            {filtered.map((entity) => {
              const selected = selectedEntityId === entity.id;
              return (
                <li key={entity.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(entity.id)}
                    className={cn(
                      'w-full border-l-4 px-4 py-3 text-left transition-colors focus:bg-accent focus:outline-none',
                      selected
                        ? 'border-l-primary bg-primary/5'
                        : 'border-l-transparent hover:bg-accent',
                    )}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-bold text-foreground">
                          {entity.name}
                        </div>
                        {entity.tradingName && entity.tradingName !== entity.name && (
                          <div className="truncate text-xs text-muted-foreground">
                            t/a {entity.tradingName}
                          </div>
                        )}
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {entity.entityTypeDisplayName}
                        </div>
                      </div>
                      <StatusBadge
                        tone={toneForLifecycleStatus(entity.status)}
                        label={entity.status}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-border" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="space-y-2 px-4 py-3">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </li>
      ))}
    </ul>
  );
}
